import React, { useEffect, useRef, useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import toast from "react-hot-toast";
import {
  FaTimes,
  FaTrash,
  FaArchive,
  FaCheck,
  FaUndo,
  FaPaperclip,
  FaPlus,
} from "react-icons/fa";
import MultiContactSearchDropdown from "../common/MultiContactSearchDropdown";
import {
  PRIORITIES,
  PRIORITY_STYLES,
  TASK_CATEGORIES,
  CATEGORY_STYLES,
  assigneesLabel,
  contactLabel,
  filterValidAssigneeIds,
  formatDateTime,
  generateChecklistId,
  getAuthUser,
  getRelationId,
  getRelationIds,
  internalContactLabel,
  parseChecklist,
  sameIdSet,
} from "../../utils/taskHelpers";
import {
  fireTaskNotification,
  notifyTaskArchived,
  notifyTaskAssigned,
  notifyTaskComment,
  notifyTaskCreated,
  notifyTaskReopened,
  notifyTaskStatusChanged,
} from "../../utils/taskEmailService";
import {
  createTask,
  createTaskComment,
  fetchTaskActivity,
  fetchTaskById,
  fetchTaskComments,
  getDefaultTaskStatus,
  getNextTaskPosition,
  logTaskActivity,
  pb,
  updateTask,
} from "../../api/pocketbase";

const quillModules = {
  toolbar: [
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const quillStyle = `
  .task-detail-quill .ql-editor {
    min-height: 120px;
    max-height: 280px;
    overflow-y: auto;
  }
  .task-detail-quill .ql-editor img {
    max-width: 100%;
    max-height: 240px;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0.5rem 0;
  }
  .task-comment-quill .ql-editor {
    min-height: 80px;
    max-height: 180px;
    overflow-y: auto;
  }
  .task-comment-quill .ql-editor img {
    max-width: 100%;
    max-height: 160px;
    height: auto;
    object-fit: contain;
    display: block;
    margin: 0.5rem 0;
  }
  .task-prose-content img {
    max-width: 100%;
    max-height: 320px;
    height: auto;
    object-fit: contain;
  }
`;

const TaskDetailModal = ({
  taskId,
  isCreateMode,
  statuses,
  contacts,
  onClose,
  onSaved,
}) => {
  const [task, setTask] = useState(null);
  const [comments, setComments] = useState([]);
  const [activity, setActivity] = useState([]);
  const [activeTab, setActiveTab] = useState("details");
  const [isLoading, setIsLoading] = useState(!isCreateMode);
  const [isSaving, setIsSaving] = useState(false);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [priority, setPriority] = useState("Medium");
  const [statusId, setStatusId] = useState("");
  const [assignedTo, setAssignedTo] = useState([]);
  const [category, setCategory] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [checklist, setChecklist] = useState([]);
  const [newChecklistText, setNewChecklistText] = useState("");

  const [commentBody, setCommentBody] = useState("");
  const [commentFile, setCommentFile] = useState(null);
  const [isSendingComment, setIsSendingComment] = useState(false);

  const [newFiles, setNewFiles] = useState([]);
  const fileInputRef = useRef(null);
  const commentFileRef = useRef(null);

  const authUser = getAuthUser();
  const completedStatus = statuses.find((s) => s.IsTerminal);
  const isCompleted = task?.expand?.Status?.IsTerminal || statusId === completedStatus?.id;
  const isTerminalStatus = (id) => statuses.find((s) => s.id === id)?.IsTerminal === true;

  const loadTaskData = async (id) => {
    setIsLoading(true);
    try {
      const [taskData, commentsData, activityData] = await Promise.all([
        fetchTaskById(id),
        fetchTaskComments(id),
        fetchTaskActivity(id),
      ]);
      setTask(taskData);
      setTitle(taskData.Title || "");
      setDescription(taskData.Description || "");
      setPriority(taskData.Priority || "Medium");
      setStatusId(getRelationId(taskData.Status) || getRelationId(taskData.expand?.Status));
      setAssignedTo(
        filterValidAssigneeIds(
          getRelationIds(taskData.AssignedTo, taskData.expand?.AssignedTo),
          contacts
        )
      );
      setCategory(taskData.Category || "");
      setDueDate(taskData.DueDate ? taskData.DueDate.slice(0, 10) : "");
      setChecklist(parseChecklist(taskData.Checklist));
      setComments(commentsData);
      setActivity(activityData);
    } catch (error) {
      console.error("Error loading task:", error);
      toast.error("Failed to load task");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const init = async () => {
      if (isCreateMode) {
        const defaultStatus = statuses.find((s) => s.IsDefault) || statuses[0];
        setStatusId(defaultStatus?.id || "");
        setIsLoading(false);
        return;
      }
      if (taskId) {
        await loadTaskData(taskId);
      }
    };
    init();
  }, [taskId, isCreateMode, statuses]);

  const getFileUrl = (record, filename) => {
    const file = Array.isArray(filename) ? filename[0] : filename;
    if (!file) return "";
    return pb.files.getUrl(record, file);
  };

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!statusId) {
      toast.error("Status is required");
      return;
    }

    setIsSaving(true);
    try {
      const validAssignedTo = filterValidAssigneeIds(assignedTo, contacts);

      const payload = {
        Title: title.trim(),
        Description: description,
        Priority: priority,
        Status: statusId,
        AssignedTo: validAssignedTo,
        Category: category || "",
        DueDate: dueDate || "",
        Checklist: checklist,
      };

      if (isCreateMode) {
        const createStatusId = statusId || (await getDefaultTaskStatus())?.id;
        if (!createStatusId) {
          toast.error("No task status available. Add statuses in PocketBase first.");
          setIsSaving(false);
          return;
        }
        const position = await getNextTaskPosition(createStatusId);

        const createPayload = {
          Title: title.trim(),
          Description: description,
          Priority: priority,
          Status: createStatusId,
          AssignedTo: filterValidAssigneeIds(assignedTo, contacts),
          Category: category || "",
          DueDate: dueDate || "",
          Checklist: checklist,
          Position: position,
          IsArchived: false,
          CreatedBy: authUser?.id || "",
        };

        let created;
        if (newFiles.length > 0) {
          const formData = new FormData();
          Object.entries(createPayload).forEach(([key, value]) => {
            if (key === "AssignedTo" && Array.isArray(value)) {
              value.forEach((id) => formData.append("AssignedTo", id));
            } else if (key === "Checklist") {
              formData.append(key, JSON.stringify(value));
            } else if (key === "IsArchived") {
              formData.append(key, "false");
            } else {
              formData.append(key, value ?? "");
            }
          });
          newFiles.forEach((file) => formData.append("Attachments", file));
          created = await createTask(formData);
        } else {
          created = await createTask(createPayload);
        }

        await logTaskActivity({
          taskId: created.id,
          actorId: authUser?.id,
          action: "created",
          message: `${contactLabel(authUser)} created this task`,
        });

        const assigneeContacts = contacts.filter((c) =>
          filterValidAssigneeIds(assignedTo, contacts).includes(c.id)
        );
        fireTaskNotification(() =>
          notifyTaskCreated({
            task: {
              ...created,
              Title: title.trim(),
              Priority: priority,
              Status: createStatusId,
              Category: category || "",
              DueDate: dueDate || "",
              AssignedTo: filterValidAssigneeIds(assignedTo, contacts),
              CreatedBy: authUser?.id || "",
              expand: {
                AssignedTo: assigneeContacts,
                CreatedBy: authUser,
              },
            },
            contacts,
            statuses,
            actorId: authUser?.id,
          })
        );

        toast.success("Task created");
        await onSaved?.();
        onClose();
        return;
      }

      const previous = task;

      if (newFiles.length > 0) {
        const formData = new FormData();
        Object.entries(payload).forEach(([key, value]) => {
          if (key === "AssignedTo" && Array.isArray(value)) {
            value.forEach((id) => formData.append("AssignedTo", id));
          } else if (key === "Checklist") {
            formData.append(key, JSON.stringify(value));
          } else {
            formData.append(key, value ?? "");
          }
        });
        newFiles.forEach((file) => formData.append("Attachments+", file));
        await updateTask(task.id, formData);
      } else {
        await updateTask(task.id, { ...payload, Checklist: checklist });
      }

      if (previous.Title !== title.trim()) {
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "updated",
          message: `${contactLabel(authUser)} updated the task title`,
        });
      }
      if (previous.Priority !== priority) {
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "priority_changed",
          message: `${contactLabel(authUser)} changed priority to ${priority}`,
          metadata: { from: previous.Priority, to: priority },
        });
      }
      if (getRelationId(previous.Status) !== statusId) {
        const oldName = statuses.find((s) => s.id === getRelationId(previous.Status))?.Name;
        const newName = statuses.find((s) => s.id === statusId)?.Name;
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "status_changed",
          message: `${contactLabel(authUser)} moved task from ${oldName} to ${newName}`,
          metadata: { from: oldName, to: newName },
        });
        fireTaskNotification(() =>
          notifyTaskStatusChanged({
            task: {
              ...task,
              Title: title.trim(),
              Priority: priority,
              Status: statusId,
              AssignedTo: validAssignedTo,
            },
            contacts,
            statuses,
            actorId: authUser?.id,
            oldStatusName: oldName,
            newStatusName: newName,
          })
        );
      }
      if (!sameIdSet(getRelationIds(previous.AssignedTo, previous.expand?.AssignedTo), validAssignedTo)) {
        const addedIds = validAssignedTo.filter(
          (id) => !getRelationIds(previous.AssignedTo, previous.expand?.AssignedTo).includes(id)
        );
        const assigneeContacts = contacts.filter((c) => validAssignedTo.includes(c.id));

        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: validAssignedTo.length ? "assigned" : "unassigned",
          message: validAssignedTo.length
            ? `${contactLabel(authUser)} assigned task to ${assigneesLabel(assigneeContacts)}`
            : `${contactLabel(authUser)} unassigned the task`,
        });

        if (addedIds.length > 0) {
          fireTaskNotification(() =>
            notifyTaskAssigned({
              task: {
                ...task,
                Title: title.trim(),
                Priority: priority,
                Status: statusId,
                Category: category || "",
                AssignedTo: validAssignedTo,
                expand: { ...task.expand, AssignedTo: assigneeContacts },
              },
              contacts,
              statuses,
              actorId: authUser?.id,
              assigneeIds: addedIds,
            })
          );
        }
      }
      if ((previous.DueDate || "").slice(0, 10) !== dueDate) {
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "due_date_changed",
          message: `${contactLabel(authUser)} updated the due date`,
        });
      }
      if (JSON.stringify(parseChecklist(previous.Checklist)) !== JSON.stringify(checklist)) {
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "checklist_updated",
          message: `${contactLabel(authUser)} updated the checklist`,
        });
      }
      if (newFiles.length > 0) {
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "attachment_added",
          message: `${contactLabel(authUser)} added ${newFiles.length} attachment(s)`,
        });
      }

      toast.success("Task saved");
      await onSaved?.();

      if (isTerminalStatus(statusId)) {
        onClose();
      } else {
        await loadTaskData(task.id);
        setNewFiles([]);
      }
    } catch (error) {
      console.error("Error saving task:", error);
      toast.error("Failed to save task");
    } finally {
      setIsSaving(false);
    }
  };

  const handleMarkComplete = async () => {
    if (!completedStatus) {
      toast.error("No completed status configured");
      return;
    }
    setStatusId(completedStatus.id);
    if (!isCreateMode && task) {
      setIsSaving(true);
      try {
        await updateTask(task.id, { Status: completedStatus.id });
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "status_changed",
          message: `${contactLabel(authUser)} marked task as completed`,
        });
        const oldName =
          task.expand?.Status?.Name ||
          statuses.find((s) => s.id === getRelationId(task.Status))?.Name ||
          "";
        fireTaskNotification(() =>
          notifyTaskStatusChanged({
            task: { ...task, Status: completedStatus.id },
            contacts,
            statuses,
            actorId: authUser?.id,
            oldStatusName: oldName,
            newStatusName: completedStatus.Name,
          })
        );
        toast.success("Task marked as completed");
        await onSaved?.();
        onClose();
      } catch (error) {
        toast.error("Failed to complete task");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleReopen = async () => {
    const reopenStatus = statuses.find((s) => s.Key === "todo") || statuses.find((s) => !s.IsTerminal);
    if (!reopenStatus) return;
    setStatusId(reopenStatus.id);
    if (!isCreateMode && task) {
      setIsSaving(true);
      try {
        await updateTask(task.id, { Status: reopenStatus.id });
        await logTaskActivity({
          taskId: task.id,
          actorId: authUser?.id,
          action: "reopened",
          message: `${contactLabel(authUser)} reopened the task`,
        });
        const oldName =
          task.expand?.Status?.Name ||
          statuses.find((s) => s.id === getRelationId(task.Status))?.Name ||
          "";
        fireTaskNotification(() =>
          notifyTaskStatusChanged({
            task: { ...task, Status: reopenStatus.id },
            contacts,
            statuses,
            actorId: authUser?.id,
            oldStatusName: oldName,
            newStatusName: reopenStatus.Name,
          })
        );
        fireTaskNotification(() =>
          notifyTaskReopened({
            task: { ...task, Status: reopenStatus.id },
            contacts,
            statuses,
            actorId: authUser?.id,
            newStatusName: reopenStatus.Name,
          })
        );
        toast.success("Task reopened");
        onSaved?.();
        await loadTaskData(task.id);
      } catch (error) {
        toast.error("Failed to reopen task");
      } finally {
        setIsSaving(false);
      }
    }
  };

  const handleArchive = async () => {
    if (!task || !window.confirm("Archive this task?")) return;
    setIsSaving(true);
    try {
      await updateTask(task.id, { IsArchived: true });
      await logTaskActivity({
        taskId: task.id,
        actorId: authUser?.id,
        action: "archived",
        message: `${contactLabel(authUser)} archived the task`,
      });
      fireTaskNotification(() =>
        notifyTaskArchived({ task, contacts, actorId: authUser?.id })
      );
      toast.success("Task archived");
      onSaved?.();
      onClose();
    } catch (error) {
      toast.error("Failed to archive task");
    } finally {
      setIsSaving(false);
    }
  };

  const handleRestore = async () => {
    if (!task) return;
    setIsSaving(true);
    try {
      await updateTask(task.id, { IsArchived: false });
      await logTaskActivity({
        taskId: task.id,
        actorId: authUser?.id,
        action: "restored",
        message: `${contactLabel(authUser)} restored the task`,
      });
      toast.success("Task restored");
      onSaved?.();
      await loadTaskData(task.id);
    } catch (error) {
      toast.error("Failed to restore task");
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddChecklistItem = () => {
    if (!newChecklistText.trim()) return;
    setChecklist((prev) => [
      ...prev,
      {
        id: generateChecklistId(),
        text: newChecklistText.trim(),
        done: false,
        order: prev.length,
      },
    ]);
    setNewChecklistText("");
  };

  const handleToggleChecklistItem = (id) => {
    setChecklist((prev) =>
      prev.map((item) => (item.id === id ? { ...item, done: !item.done } : item))
    );
  };

  const handleRemoveChecklistItem = (id) => {
    setChecklist((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSendComment = async () => {
    if (!commentBody.trim() || commentBody === "<p><br></p>") {
      toast.error("Please enter a comment");
      return;
    }
    setIsSendingComment(true);
    try {
      const formData = new FormData();
      formData.append("Task", task.id);
      formData.append("Author", authUser?.id || "");
      formData.append("Body", commentBody);
      if (commentFile) formData.append("Attachment", commentFile);

      await createTaskComment(formData);
      await logTaskActivity({
        taskId: task.id,
        actorId: authUser?.id,
        action: "comment_added",
        message: `${contactLabel(authUser)} added a comment`,
      });

      fireTaskNotification(() =>
        notifyTaskComment({
          task,
          contacts,
          actorId: authUser?.id,
          commentBody,
        })
      );

      setCommentBody("");
      setCommentFile(null);
      if (commentFileRef.current) commentFileRef.current.value = "";
      const [commentsData, activityData] = await Promise.all([
        fetchTaskComments(task.id),
        fetchTaskActivity(task.id),
      ]);
      setComments(commentsData);
      setActivity(activityData);
      toast.success("Comment added");
    } catch (error) {
      console.error("Error adding comment:", error);
      toast.error("Failed to add comment");
    } finally {
      setIsSendingComment(false);
    }
  };

  const existingAttachments = task?.Attachments || [];

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4">
      <style>{quillStyle}</style>
      <div className="flex max-h-[95vh] w-full max-w-4xl flex-col overflow-hidden rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <div className="flex shrink-0 items-center justify-between border-b border-gray-200 px-4 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {isCreateMode ? "New Task" : "Task Details"}
            </h2>
            {!isCreateMode && task && (
              <p className="text-sm text-gray-500">
                Created {formatDateTime(task.created)}
                {internalContactLabel(task.expand?.CreatedBy) &&
                  ` by ${internalContactLabel(task.expand.CreatedBy)}`}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100"
          >
            <FaTimes />
          </button>
        </div>

        {!isCreateMode && (
          <div className="flex shrink-0 gap-1 overflow-x-auto border-b border-gray-100 px-4 sm:px-6">
            {["details", "comments", "activity"].map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`shrink-0 border-b-2 px-4 py-3 text-sm font-medium capitalize transition ${
                  activeTab === tab
                    ? "border-[#90c740] text-[#90c740]"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
                {tab === "comments" && comments.length > 0 && (
                  <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-xs">
                    {comments.length}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6">
          {isLoading ? (
            <div className="space-y-4 py-8">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded bg-gray-100" />
              ))}
            </div>
          ) : (
            <>
              {(isCreateMode || activeTab === "details") && (
                <div className="space-y-5">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Title *</label>
                    <input
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                      placeholder="Task title"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">Description</label>
                    <div className="task-detail-quill">
                      <ReactQuill theme="snow" value={description} onChange={setDescription} modules={quillModules} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Priority</label>
                      <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                      <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs ${PRIORITY_STYLES[priority]}`}>
                        {priority}
                      </span>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
                      <select
                        value={statusId}
                        onChange={(e) => setStatusId(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                      >
                        {statuses.map((s) => (
                          <option key={s.id} value={s.id}>{s.Name}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Category</label>
                      <select
                        value={category}
                        onChange={(e) => setCategory(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                      >
                        <option value="">Select category</option>
                        {TASK_CATEGORIES.map((cat) => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                      {category && (
                        <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs ${CATEGORY_STYLES[category] || "bg-gray-100 text-gray-700"}`}>
                          {category}
                        </span>
                      )}
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Assign To</label>
                      <MultiContactSearchDropdown
                        contacts={contacts}
                        value={assignedTo}
                        onChange={setAssignedTo}
                        placeholder="Select team members"
                      />
                    </div>

                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">Due Date</label>
                      <input
                        type="date"
                        value={dueDate}
                        onChange={(e) => setDueDate(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Checklist</label>
                    <div className="space-y-2 rounded-lg border border-gray-200 p-3">
                      {checklist.map((item) => (
                        <div key={item.id} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={item.done}
                            onChange={() => handleToggleChecklistItem(item.id)}
                            className="h-4 w-4 rounded text-[#90c740]"
                          />
                          <span className={`flex-1 text-sm ${item.done ? "text-gray-400 line-through" : "text-gray-800"}`}>
                            {item.text}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleRemoveChecklistItem(item.id)}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </div>
                      ))}
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newChecklistText}
                          onChange={(e) => setNewChecklistText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleAddChecklistItem())}
                          placeholder="Add checklist item..."
                          className="flex-1 rounded border border-gray-300 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#90c740]"
                        />
                        <button
                          type="button"
                          onClick={handleAddChecklistItem}
                          className="rounded bg-gray-100 px-3 py-1.5 text-sm hover:bg-gray-200"
                        >
                          <FaPlus />
                        </button>
                      </div>
                      {checklist.length > 0 && (
                        <p className="text-xs text-gray-500">
                          {checklist.filter((i) => i.done).length}/{checklist.length} completed
                        </p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700">Attachments</label>
                    <div className="rounded-lg border border-gray-200 p-3">
                      {existingAttachments.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-2">
                          {existingAttachments.map((file) => (
                            <a
                              key={file}
                              href={getFileUrl(task, file)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200"
                            >
                              <FaPaperclip /> {file}
                            </a>
                          ))}
                        </div>
                      )}
                      <input
                        ref={fileInputRef}
                        type="file"
                        multiple
                        onChange={(e) => setNewFiles(Array.from(e.target.files || []))}
                        className="text-sm"
                      />
                      {newFiles.length > 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          {newFiles.length} new file(s) selected — save to upload
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {!isCreateMode && activeTab === "comments" && (
                <div className="space-y-4">
                  <div className="space-y-3">
                    {comments.length === 0 ? (
                      <p className="py-8 text-center text-gray-500">No comments yet</p>
                    ) : (
                      comments.map((comment) => (
                        <div key={comment.id} className="rounded-lg border border-gray-200 p-4">
                          <div className="mb-2 flex items-center justify-between">
                            <span className="font-medium text-gray-900">
                              {internalContactLabel(comment.expand?.Author) || "—"}
                            </span>
                            <span className="text-xs text-gray-500">{formatDateTime(comment.created)}</span>
                          </div>
                          <div
                            className="task-prose-content prose prose-sm max-w-none text-gray-700"
                            dangerouslySetInnerHTML={{ __html: comment.Body }}
                          />
                          {comment.Attachment && (
                            <a
                              href={getFileUrl(comment, comment.Attachment)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="mt-2 inline-flex items-center gap-1 text-sm text-[#90c740] hover:underline"
                            >
                              <FaPaperclip /> View attachment
                            </a>
                          )}
                        </div>
                      ))
                    )}
                  </div>

                  <div className="rounded-lg border border-gray-200 p-4">
                    <label className="mb-2 block text-sm font-medium text-gray-700">Add Comment</label>
                    <div className="task-comment-quill">
                      <ReactQuill theme="snow" value={commentBody} onChange={setCommentBody} modules={quillModules} />
                    </div>
                    <div className="mt-3 flex flex-wrap items-center gap-3">
                      <input
                        ref={commentFileRef}
                        type="file"
                        onChange={(e) => setCommentFile(e.target.files?.[0] || null)}
                        className="text-sm"
                      />
                      <button
                        type="button"
                        onClick={handleSendComment}
                        disabled={isSendingComment}
                        className="ml-auto rounded-lg bg-[#90c740] px-4 py-2 text-sm font-medium text-white hover:bg-[#7ebd2e] disabled:opacity-50"
                      >
                        {isSendingComment ? "Sending..." : "Post Comment"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {!isCreateMode && activeTab === "activity" && (
                <div className="space-y-3">
                  {activity.length === 0 ? (
                    <p className="py-8 text-center text-gray-500">No activity yet</p>
                  ) : (
                    activity.map((entry) => (
                      <div key={entry.id} className="flex gap-3 border-b border-gray-100 pb-3">
                        <div className="mt-1 h-2 w-2 shrink-0 rounded-full bg-[#90c740]" />
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-gray-800">{entry.Message}</p>
                          <p className="mt-0.5 text-xs text-gray-500">
                            {formatDateTime(entry.created)}
                            {entry.Action && ` · ${entry.Action.replace(/_/g, " ")}`}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2 border-t border-gray-200 px-4 py-4 sm:px-6">
          {!isCreateMode && task && (
            <>
              {task.IsArchived ? (
                <button
                  type="button"
                  onClick={handleRestore}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaUndo /> Restore
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleArchive}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                >
                  <FaArchive /> Archive
                </button>
              )}
              {!isCompleted ? (
                <button
                  type="button"
                  onClick={handleMarkComplete}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-lg border border-green-300 px-3 py-2 text-sm text-green-700 hover:bg-green-50"
                >
                  <FaCheck /> Mark Complete
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleReopen}
                  disabled={isSaving}
                  className="flex items-center gap-1.5 rounded-lg border border-blue-300 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50"
                >
                  <FaUndo /> Reopen
                </button>
              )}
            </>
          )}

          <div className="ml-auto flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {(isCreateMode || activeTab === "details") && (
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || isLoading}
                className="rounded-lg bg-[#90c740] px-4 py-2 text-sm font-medium text-white hover:bg-[#7ebd2e] disabled:opacity-50"
              >
                {isSaving ? "Saving..." : isCreateMode ? "Create Task" : "Save Changes"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaskDetailModal;
